import { GoogleGenAI, GenerateContentResponse, Type } from "@google/genai";
import { AIService } from "../types";

export class GeminiService implements AIService {
  /**
   * Generates content for a workflow node execution.
   * Uses 'gemini-3-flash-preview' as the default model for efficient processing.
   * Re-instantiates the client on each call to ensure process.env.API_KEY is current.
   */
  async generateWorkflowCompletion(prompt: string, model: string = 'gemini-3-flash-preview'): Promise<string> {
    try {
      const ai = new GoogleGenAI({ apiKey: process.env.API_KEY });
      const response: GenerateContentResponse = await ai.models.generateContent({
        model: model,
        contents: prompt,
      });

      // The response.text is a property, not a method.
      return response.text || "";
    } catch (error) {
      console.error("Gemini API Error:", error);
      throw new Error("Failed to generate content from Gemini.");
    }
  }

  /**
   * Evaluates a logical condition using Gemini's structured output.
   * Configures responseSchema for reliable boolean results.
   */
  async evaluateCondition(context: any, condition: string): Promise<boolean> {
     const prompt = `
      Context: ${JSON.stringify(context)}
      Condition to evaluate: ${condition}
      Return ONLY valid JSON: {"result": boolean}
     `;

     try {
       const ai = new GoogleGenAI({ apiKey: process.env.API_KEY });
       const response = await ai.models.generateContent({
          model: 'gemini-3-flash-preview',
          contents: prompt,
          config: {
              responseMimeType: "application/json",
              responseSchema: {
                type: Type.OBJECT,
                properties: {
                  result: {
                    type: Type.BOOLEAN,
                    description: 'The evaluated boolean result'
                  }
                },
                required: ['result']
              }
          }
       });

       const json = JSON.parse(response.text || "{}");
       return !!json.result;
     } catch (e) {
       console.error("Gemini evaluation error:", e);
       // Fallback logic for simulation resilience
       return Math.random() > 0.5;
     }
  }
}

export const geminiService = new GeminiService();